import React from 'react';
import { ExternalLink, Info, CheckCircle, Package } from 'lucide-react';
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Button, Chip, Card, CardBody } from '@heroui/react';
import { Product } from '@/services/apis/products';
import { useTranslation } from '@/hooks/useTranslation';

interface ToolModalProps {
  product: Product;
  isOpen: boolean;
  onClose: () => void;
}

const ToolModal: React.FC<ToolModalProps> = ({ product, isOpen, onClose }) => {
  const { t } = useTranslation('products');
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'operational':
        return 'success' as const;
      case 'beta':
        return 'warning' as const;
      case 'maintenance':
        return 'danger' as const;
      default:
        return 'default' as const;
    }
  };

  const handleOpenTool = () => {
    if (product.url) {
      window.open(product.url, '_blank', 'noopener,noreferrer');
      onClose();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="2xl"
      scrollBehavior="inside"
      classNames={{
        base: 'bg-white dark:bg-gray-900',
        header: 'border-b border-gray-200 dark:border-gray-700',
        footer: 'border-t border-gray-200 dark:border-gray-700',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg">
                {product.icon ? (
                  <span className="text-2xl">{product.icon}</span>
                ) : (
                  <Package className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">{product.name}</h2>
                {product.tagline && <p className="text-sm text-gray-500 dark:text-gray-400">{product.tagline}</p>}
              </div>
            </ModalHeader>

            <ModalBody>
              {/* Status and Category */}
              <div className="flex items-center gap-4 mb-4">
                <Chip color={getStatusColor(product.status)} variant="flat" size="sm">
                  {t(`status.${product.status}`)}
                </Chip>
                {product.category && (
                  <Chip color="primary" variant="flat" size="sm">
                    {product.category.name}
                  </Chip>
                )}
              </div>

              {/* Description */}
              {product.description && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {t('modal.about', { name: product.name })}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300">{product.description}</p>
                </div>
              )}

              {/* Key Features */}
              {product.features && product.features.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('modal.keyFeatures')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {product.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-600 dark:text-gray-300">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Metrics */}
              {product.metrics && Object.keys(product.metrics).length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {t('modal.performanceMetrics')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {Object.entries(product.metrics).map(([key, value]) => (
                      <Card key={key} className="bg-gray-50 dark:bg-gray-800">
                        <CardBody className="text-center p-4">
                          <div className="text-2xl font-bold text-gray-900 dark:text-white">{String(value)}</div>
                          <div className="text-sm text-gray-600 dark:text-gray-400 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Target Users */}
              {product.targetUsers && product.targetUsers.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">{t('modal.targetUsers')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.targetUsers.map((user, index) => (
                      <Chip key={index} size="sm" variant="flat">
                        {user}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              {/* Slack Channels */}
              {product.slackChannels && product.slackChannels.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {t('modal.slackChannels')}
                  </h3>
                  <Card className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border border-purple-200 dark:border-purple-800">
                    <CardBody className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {product.slackChannels.map((channel, index) => (
                          <Chip
                            key={index}
                            variant="bordered"
                            className="border-purple-200 dark:border-purple-700 text-purple-700 dark:text-purple-300"
                            startContent={
                              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52-2.523A2.528 2.528 0 0 1 5.042 10.12h2.52v2.522a2.528 2.528 0 0 1-2.52 2.523zm0-6.341H2.52A2.528 2.528 0 0 0 0 11.347a2.528 2.528 0 0 0 2.52 2.523h2.522v-2.523V8.824z" />
                                <path d="M18.958 8.824a2.528 2.528 0 0 1 2.52 2.523A2.528 2.528 0 0 1 18.958 13.87h-2.52v-2.522a2.528 2.528 0 0 1 2.52-2.523zm0 6.341h2.522A2.528 2.528 0 0 0 24 12.642a2.528 2.528 0 0 0-2.52-2.523h-2.522v2.523v2.522z" />
                                <path d="M8.824 5.042a2.528 2.528 0 0 1 2.523-2.52A2.528 2.528 0 0 1 13.87 5.042v2.52h-2.523A2.528 2.528 0 0 1 8.824 5.042zm6.341 0v-2.52A2.528 2.528 0 0 1 17.688 0a2.528 2.528 0 0 1 2.523 2.52v2.522h-2.523A2.528 2.528 0 0 1 15.165 5.042z" />
                              </svg>
                            }
                          >
                            {channel}
                          </Chip>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                </div>
              )}

              {/* Contact Person */}
              {product.contactPerson?.name && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    {t('modal.contactPerson')}
                  </h3>
                  <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800">
                    <CardBody className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                            <span className="text-white font-semibold text-lg">
                              {product.contactPerson.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="space-y-3">
                            <div>
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {product.contactPerson.name}
                              </h4>
                              {product.contactPerson?.role && (
                                <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                                  {product.contactPerson.role}
                                </p>
                              )}
                            </div>
                            <Button
                              as="a"
                              href={`mailto:${product.contactPerson.email}`}
                              variant="bordered"
                              size="sm"
                              className="border-blue-200 dark:border-blue-700 text-blue-600 dark:text-blue-400"
                              startContent={
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                  />
                                </svg>
                              }
                            >
                              {product.contactPerson.email}
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </div>
              )}

              {/* Tool Access Information */}
              <Card className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <CardBody className="p-4">
                  <div className="flex items-start space-x-3">
                    <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-1">{t('modal.toolAccess')}</h4>
                      <p className="text-sm text-blue-700 dark:text-blue-300">
                        {product.url
                          ? t('modal.toolAccessWithUrl', { name: product.name })
                          : t('modal.toolAccessWithoutUrl')}
                      </p>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </ModalBody>

            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                {t('modal.close')}
              </Button>
              <Button
                color="primary"
                variant="solid"
                className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white border-0"
                startContent={<ExternalLink className="w-4 h-4" />}
                onPress={handleOpenTool}
                isDisabled={!product.url}
              >
                {t('modal.openTool')}
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ToolModal;
